<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-12">
          <h6 class="h2 text-white d-inline-block mb-0">Editar Recibo</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb />
          </nav>
        </div>
      </div>
    </base-header>

    <div v-if="order && receipt" class="container-fluid mt--6">
      <div class="row justify-content-center">
        <div class="col-12 card-wrapper">
          <div class="card">
            <div class="card-header">
              <div class="card-body">
                <order-info :receipt="receipt" :order="order" :edit="true" @get-quotation="setDollarQuotation" />

                <div v-if="canLoad">
                  <hr />

                  <concept-payment-form
                    ref="conceptPaymentForm"
                    class="mb-4"
                    :receipt="receipt"
                    :updated-debt-in-dollars="debtInDollars"
                    :updated-debt-in-pesos="debtInPesos"
                    @addItem="addItem"
                  />

                  <concept-payment-list
                    v-if="hasItems"
                    class="mb-4"
                    :items="receipt.conceptPayments"
                    :edit="true"
                    @deleteItem="deleteItem"
                  />

                  <payment-by-months v-if="order && receipt" class="mb-4" :order="order" :receipt="receipt" />

                  <debt-info
                    class="mb-4"
                    :order-dollar-quote="order.dollarQuote"
                    :current-dollar-quote="currentDollarQuotation"
                    :debt-in-dollars="debtInDollars"
                    :debt-in-pesos="debtInOrderPesos"
                  />
                </div>
              </div>

              <div v-if="canLoad" class="class-footer">
                <div class="text-right">
                  <base-button
                    v-if="canConfirm"
                    type="success"
                    :disabled="!hasItems || debtInDollars < 0"
                    @click="onConfirm"
                  >
                    Confirmar
                  </base-button>
                  <span v-if="debtInPesos < 0" class="text-danger d-block">Monto a cancelar excedido</span>
                  <base-button v-if="canSave" type="primary" :disabled="!hasItems || debtInDollars < 0" @click="onSave">
                    Guardar
                  </base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import { mapState, mapActions, mapGetters } from 'vuex';

import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import { subtract, divide } from '@/util/math';

import OrderInfo from './List/OrderInfo.vue';
import DebtInfo from './List/DebtInfo.vue';
import PaymentByMonths from './List/PaymentByMonths.vue';
import ConceptPaymentForm from './Forms/ConceptPaymentForm.vue';
import ConceptPaymentList from './List/ConceptPaymentList.vue';
import OrdersService from '@/services/resources/orders.service';

export default {
  name: 'ReceiptEdit',
  components: {
    RouteBreadCrumb,
    OrderInfo,
    DebtInfo,
    PaymentByMonths,
    ConceptPaymentForm,
    ConceptPaymentList,
  },

  data() {
    return {
      order: null,
      receipt: null,
      debtInDollar: 0,
      debtInPesos: 0,
      debtInOrderPesos: 0,
    };
  },

  computed: {
    ...mapGetters('dollarQuotation', ['currentDollarQuotation']),

    ...mapState('models/order', { orderDebt: 'debt' }),

    hasItems() {
      return Boolean(this.receipt.conceptPayments.length > 0);
    },

    canLoad() {
      return Boolean(this.orderDebt && this.receipt.dollarQuotation !== 0);
    },

    canConfirm() {
      return !this.receipt.compliance && this.order.state.name === OrdersService._STATE_ACCEPTED;
    },

    canSave() {
      return this.order.state.name === OrdersService._STATE_ACCEPTED;
    },
  },

  async created() {
    await this.fetchReceipt();

    await this.fetchOrderDebt({
      orderId: this.receipt.orderId,
      dollarQuotation: this.receipt.dollarQuotation,
      receiptId: this.receipt.id,
    });

    this.fetchOrder();

    this.updateDebt();

    this.addAmountInDollarToConceptPayments();
  },

  methods: {
    ...mapActions('models/order', ['fetchOrderDebt']),

    updateDebt() {
      this.debtInDollars = this.getDebt(this.orderDebt.dollar, 'amountInDollars');
      this.debtInPesos = this.getDebt(this.orderDebt.pesos, 'amountInPesos');
      this.debtInOrderPesos = this.getDebt(this.orderDebt.pesosOrder, 'amountInPesos');
    },

    getDebt(total, prop) {
      const canceled = [...this.receipt.conceptPayments].reduce(
        (canceled, conceptPayment) => Number(canceled) + Number(conceptPayment[prop]),
        0
      );

      return subtract(total, canceled);
    },

    addItem(item) {
      this.receipt.conceptPayments.push(item);
      this.updateDebt();
      this.$refs.conceptPaymentForm.clean();
    },

    deleteItem(_, index) {
      this.receipt.conceptPayments.splice(index, 1);
      this.updateDebt();
    },

    setDollarQuotation(dollarQuotation) {
      this.receipt.dollarQuotation = Number(dollarQuotation);
      this.fetchOrderDebt({
        orderId: this.receipt.orderId,
        dollarQuotation: this.receipt.dollarQuotation,
        receiptId: this.receipt.id,
      });

      this.updateDebt();

      this.addAmountInDollarToConceptPayments();
    },

    addAmountInDollarToConceptPayments() {
      if (this.receipt.conceptPayments.length > 0) {
        this.receipt.conceptPayments = this.receipt.conceptPayments.map((concept) => {
          const amountInDollars = divide(concept.amountInPesos, this.receipt.dollarQuotation);

          return {
            ...concept,
            amountInDollars,
          };
        });
      }
    },

    async onSave() {
      return this.$services
        .resource('receipts')
        .update(this.receipt.id, this.receipt)
        .then(() => {
          swal({
            title: 'Listo!',
            text: 'El Recibo fue editado con éxito',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-success',
            type: 'success',
          }).then(() => {
            this.$router.push({ name: 'ReceiptList' });
          });
        })
        .catch((error) => this.$error(error.response.data.message));
    },

    onConfirm() {
      this.$services
        .resource('receipts')
        .confirm(this.receipt.id)
        .then(() => {
          swal({
            title: 'Recibo confirmado con éxito',
            type: 'success',
            confirmButtonText: 'Aceptar',
          }).then(() => this.$router.push({ name: 'ReceiptAcceptedOrders' }));
        })
        .catch((err) => {
          swal({
            title: 'Error!',
            text: err.response.data.message,
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-primary',
            type: 'error',
          });
        });
    },

    fetchOrder() {
      const params = {
        include: ['producer.distributor.seller', 'state'],
        append: ['total', 'total-in-pesos'],
      };

      this.$services
        .resource('orders')
        .show(this.receipt.orderId, params)
        .then((res) => res.data.data)
        .then((order) => (this.order = order));
    },

    fetchReceipt() {
      const params = {
        include: ['order.producer.distributor.seller', 'conceptPayments.conceptPaymentType'],
        append: ['total'],
      };

      return this.$services
        .resource('receipts')
        .show(this.$route.params.receiptId, params)
        .then((res) => res.data.data)
        .then((receipt) => (this.receipt = receipt));
    },
  },
};
</script>
